import apiInstance from "api";

/**
 * Product Configurator API Service
 * Handles all API calls for the product configurator backoffice
 */
export const ProductConfiguratorApiService = {
  // ==================== Configuration Templates ====================
  
  /**
   * Get all configuration templates
   * @param {Object} queryParams - Filter parameters (organisationId, keyword, pageNumber, pageSize, etc.)
   */
  getTemplates: async (queryParams) => {
    return await apiInstance.get("/admin/product-configurator/templates", {
      params: {
        ...queryParams,
      },
    });
  },

  /**
   * Get a single configuration template by ID
   * @param {number} templateId - Template ID
   */
  getTemplate: async (templateId) => {
    return await apiInstance.get(`/admin/product-configurator/templates/${templateId}`);
  },

  /**
   * Get configuration template by product ID
   * @param {number} productId - Product ID
   */
  getTemplateByProduct: async (productId) => {
    return await apiInstance.get(`/admin/product-configurator/templates/by-product/${productId}`);
  },

  /**
   * Create a new configuration template
   * @param {Object} body - Template data
   */
  createTemplate: async (body) => {
    return await apiInstance.post("/admin/product-configurator/templates", body);
  },

  /**
   * Update an existing configuration template
   * @param {Object} body - Template data with ID
   */
  updateTemplate: async (body) => {
    return await apiInstance.put(`/admin/product-configurator/templates/${body.id}`, body);
  },

  /**
   * Delete a configuration template
   * @param {number} templateId - Template ID
   */
  deleteTemplate: async (templateId) => {
    return await apiInstance.delete(`/admin/product-configurator/templates/${templateId}`);
  },

  // ==================== Option Groups ====================

  /**
   * Get all option groups for a template
   * @param {number} templateId - Template ID
   */
  getOptionGroups: async (templateId) => {
    return await apiInstance.get(`/admin/product-configurator/templates/${templateId}/option-groups`);
  },

  /**
   * Get a single option group by ID
   * @param {number} groupId - Option Group ID
   */
  getOptionGroup: async (groupId) => {
    return await apiInstance.get(`/admin/product-configurator/option-groups/${groupId}`);
  },

  /**
   * Create a new option group
   * @param {Object} body - Option group data
   */
  createOptionGroup: async (body) => {
    return await apiInstance.post("/admin/product-configurator/option-groups", body);
  },

  /**
   * Update an existing option group
   * @param {Object} body - Option group data with ID
   */
  updateOptionGroup: async (body) => {
    return await apiInstance.put(`/admin/product-configurator/option-groups/${body.id}`, body);
  },

  /**
   * Delete an option group
   * @param {number} groupId - Option Group ID
   */
  deleteOptionGroup: async (groupId) => {
    return await apiInstance.delete(`/admin/product-configurator/option-groups/${groupId}`);
  },

  // ==================== Options ====================

  /**
   * Get all options for an option group
   * @param {number} groupId - Option Group ID
   */
  getOptions: async (groupId) => {
    return await apiInstance.get(`/admin/product-configurator/option-groups/${groupId}/options`);
  },

  /**
   * Get a single option by ID
   * @param {number} optionId - Option ID
   */
  getOption: async (optionId) => {
    return await apiInstance.get(`/admin/product-configurator/options/${optionId}`);
  },

  /**
   * Create a new option
   * @param {Object} body - Option data
   */
  createOption: async (body) => {
    return await apiInstance.post("/admin/product-configurator/options", body);
  },

  /**
   * Update an existing option
   * @param {Object} body - Option data with ID
   */
  updateOption: async (body) => {
    return await apiInstance.put(`/admin/product-configurator/options/${body.id}`, body);
  },

  /**
   * Delete an option
   * @param {number} optionId - Option ID
   */
  deleteOption: async (optionId) => {
    return await apiInstance.delete(`/admin/product-configurator/options/${optionId}`);
  },

  // ==================== Rules ====================

  /**
   * Get all rules for a template
   * @param {number} templateId - Template ID
   */
  getRules: async (templateId) => {
    return await apiInstance.get(`/admin/product-configurator/templates/${templateId}/rules`);
  },

  /**
   * Get a single rule by ID
   * @param {number} ruleId - Rule ID
   */
  getRule: async (ruleId) => {
    return await apiInstance.get(`/admin/product-configurator/rules/${ruleId}`);
  },

  /**
   * Create a new rule
   * @param {Object} body - Rule data
   */
  createRule: async (body) => {
    return await apiInstance.post("/admin/product-configurator/rules", body);
  },

  /**
   * Update an existing rule
   * @param {Object} body - Rule data with ID
   */
  updateRule: async (body) => {
    return await apiInstance.put(`/admin/product-configurator/rules/${body.id}`, body);
  },

  /**
   * Delete a rule
   * @param {number} ruleId - Rule ID
   */
  deleteRule: async (ruleId) => {
    return await apiInstance.delete(`/admin/product-configurator/rules/${ruleId}`);
  },

  // ==================== Pricing Rules ====================

  /**
   * Get all pricing rules for a template
   * @param {number} templateId - Template ID
   */
  getPricingRules: async (templateId) => {
    return await apiInstance.get(`/admin/product-configurator/templates/${templateId}/pricing-rules`);
  },

  /**
   * Get a single pricing rule by ID
   * @param {number} ruleId - Pricing Rule ID
   */
  getPricingRule: async (ruleId) => {
    return await apiInstance.get(`/admin/product-configurator/pricing-rules/${ruleId}`);
  },

  /**
   * Create a new pricing rule
   * @param {Object} body - Pricing rule data
   */
  createPricingRule: async (body) => {
    return await apiInstance.post("/admin/product-configurator/pricing-rules", body);
  },

  /**
   * Update an existing pricing rule
   * @param {Object} body - Pricing rule data with ID
   */
  updatePricingRule: async (body) => {
    return await apiInstance.put(`/admin/product-configurator/pricing-rules/${body.id}`, body);
  },

  /**
   * Delete a pricing rule
   * @param {number} ruleId - Pricing Rule ID
   */
  deletePricingRule: async (ruleId) => {
    return await apiInstance.delete(`/admin/product-configurator/pricing-rules/${ruleId}`);
  },

  // ==================== Customer Configurations (Admin View) ====================

  /**
   * Get all customer configurations (admin view)
   * @param {Object} queryParams - Filter parameters
   */
  getConfigurations: async (queryParams) => {
    return await apiInstance.get("/admin/product-configurator/configurations", {
      params: {
        ...queryParams,
      },
    });
  },

  /**
   * Get a single configuration by ID
   * @param {number} configId - Configuration ID
   */
  getConfiguration: async (configId) => {
    return await apiInstance.get(`/admin/product-configurator/configurations/${configId}`);
  },

  /**
   * Get configuration items for a configuration
   * @param {number} configId - Configuration ID
   */
  getConfigurationItems: async (configId) => {
    return await apiInstance.get(`/admin/product-configurator/configurations/${configId}/items`);
  },
};

export default ProductConfiguratorApiService;
